// src/components/draft/SimplifiedPicksTable.js - Simplified Draft Picks Table
import React, { useMemo, useCallback } from 'react';
import { DynastyTable } from '../../services/tableService';
import { createDraftPicksColumns } from '../../services/tables/draftColumns';

/**
 * Simplified picks table showing only: Team, Round, Number, Player, Price
 */
const SimplifiedPicksTable = ({ picks, userTeamId, onPlayerClick }) => {
  // Create base columns, then modify widths using the pattern
  const columns = useMemo(() => {
    const baseColumns = createDraftPicksColumns({
      onPlayerClick: onPlayerClick,
      isYourTurn: false,
      userTeamId: userTeamId
    });

    // Filter and reorder: Round, Pick#, Player, Pos, Team, Price
    const columnOrder = ['round', 'overall_pick_number', 'player_name', 'position', 'team_name', 'baseline_price'];
    const filtered = columnOrder
      .map(key => baseColumns.find(col => col.key === key))
      .filter(Boolean);

    // Modify column widths for readable display
    const modified = filtered.map(col => {
      if (col.key === 'round') {
        return { ...col, width: 40, minWidth: 40, title: 'Rd' };
      } else if (col.key === 'overall_pick_number') {
        return { ...col, width: 40, minWidth: 40, title: '#' };
      } else if (col.key === 'team_name') {
        return { ...col, width: 100, minWidth: 100 };
      } else if (col.key === 'player_name') {
        return { ...col, width: 120, minWidth: 120 };
      } else if (col.key === 'position') {
        return { ...col, width: 50, minWidth: 50, title: 'Pos' };
      } else if (col.key === 'baseline_price') {
        return { ...col, width: 60, minWidth: 60, title: 'Price' };
      }
      return col;
    });

    return modified;
  }, [userTeamId, onPlayerClick]);

  // Wrap render functions to work with flat-data signature: (value, flatData, index)
  const flatDataColumns = useMemo(() => {
    return columns.map(col => {
      if (col.render) {
        const originalRender = col.render;
        return {
          ...col,
          render: (value, flatData, index) => {
            // flatData is the entire pick object
            return originalRender(flatData);
          }
        };
      }
      return col;
    });
  }, [columns]);

  // Add border-t between rounds (when round changes)
  const getRowClassName = useCallback((rowData, index, prevRowData) => {
    if (!prevRowData) return ''; // First row, no border
    const currentRound = rowData?.round || rowData?.round_number;
    const prevRound = prevRowData?.round || prevRowData?.round_number;
    if (currentRound !== prevRound) {
      return 'border-t-2 border-yellow-500/50';
    }
    return '';
  }, []);

  return (
    <DynastyTable
      data={picks || []}
      columns={flatDataColumns}
      initialSort={{ key: 'overall_pick_number', direction: 'asc' }}
      tableType="flat-data"
      minWidth="auto"
      enableHorizontalScroll={true}
      maxHeight="500px"
      stickyHeader={true}
      thinScrollbar={true}
      getRowClassName={getRowClassName}
    />
  );
};

export default SimplifiedPicksTable;
